import React, {useCallback, useEffect, useState} from 'react'
import {
  useLocationLogEntry,
  useLocationLogEntryType,
  FormBuilderFormRenderer,
  useRPCContext,
  RPCs,
  Alerts,
  ErrorLoadingRetry,
  IdLabel,
} from '@therms/react-modules'
import {FormLayout, Loading, useModalContext} from '@therms/atalaya'
import {Formik} from 'formik'
import {getDevice} from '@therms/web-js'

interface EntryFormProps {
  entryId?: string
  entryTypeId?: string
  locationId: string
  onCancel: () => void
  onComplete: () => void
}

function EntryForm({
  entryId,
  entryTypeId,
  locationId,
  onCancel,
  onComplete,
}: EntryFormProps) {
  const [saving, setSaving] = useState(false)

  const {rpcClient} = useRPCContext()

  const modalXCtx = useModalContext()

  const {data: entryData, loading: entryLoading} = useLocationLogEntry(
    entryId ? {entryId} : undefined,
  )
  const {
    data: entryTypeData,
    loading: entryTypeLoading,
    refresh,
  } = useLocationLogEntryType(
    // the `entryData` has the entryType if we're editing an existing entryId
    !entryId && entryTypeId ? {entryTypeId} : undefined,
  )

  const [formBuilderErrors, setFormBuilderErrors] = useState<
    {[key: string]: string} | undefined
  >()

  useEffect(() => {
    if (modalXCtx?.setHeader && entryData?.entryType.name) {
      modalXCtx.setHeader(
        <div>
          <span className="text-lg font-medium">
            {entryData.entryType.name}
          </span>{' '}
          <IdLabel
            className="text-color-subtle text-sm font-bold tracking-wide"
            idLabel={entryData.entry.idLabel}
          />
        </div>,
      )
    }
  }, [entryData])

  const save = useCallback(
    async (values) => {
      setSaving(true)

      try {
        await rpcClient.call(
          entryId
            ? RPCs.Locations.UpdateLocationLogEntry.v2
            : RPCs.Locations.CreateLocationLogEntry.v2,
          {
            entry: {
              ...values,
              device: getDevice(),
            },
            locationId,
          },
        )

        onComplete()

        Alerts.success(`Entry saved`)
      } catch (err: any) {
        setSaving(false)

        Alerts.error('There was a problem saving the Entry', err.message)

        console.log('save() err', err)
      }
    },
    [entryId, onComplete],
  )

  const handleDelete = useCallback(async () => {
    try {
      await rpcClient.call(RPCs.Locations.DeleteLocationLogEntry.v1, {
        entryId,
      })

      onComplete()
    } catch (err: any) {
      console.log('handleDelete() err', err)
    }
  }, [entryId, onComplete])

  if ((entryTypeLoading || entryLoading) && !entryTypeData) {
    return (
      <div className="h-20 w-full text-center">
        <Loading message="Loading Entry" messagePosition="vertical" />
      </div>
    )
  }

  const formId =
    entryData?.entry?.formInput?.formId || entryTypeData?.entryType?.formId

  if (!formId) return <ErrorLoadingRetry onRetry={refresh} />

  return (
    <Formik
      enableReinitialize
      initialValues={entryData?.entry || {entryTypeId, formInput: {fields: {}}}}
      onSubmit={save}
      // large forms are affected by this CORE-3257
      validateOnChange={false}
    >
      {({setFieldValue, submitForm, values}) => (
        <FormLayout>
          {saving && <Loading message="Saving Entry" overlay />}

          <FormBuilderFormRenderer
            dateTimeEndFieldId={
              (entryTypeData || entryData)?.entryType.timestampEndFieldId
            }
            dateTimeStartFieldId={
              (entryTypeData || entryData)?.entryType.timestampFieldId
            }
            initialValue={entryData?.entry.formInput?.fields}
            formId={formId}
            formValues={values.formInput.fields}
            onFormErrorStateChange={setFormBuilderErrors}
            onFormInputChange={(_values) => {
              setFieldValue('formInput', _values)
            }}
          />

          <FormLayout.Controls
            disabled={!!formBuilderErrors}
            onCancel={onCancel}
            onDelete={entryId ? handleDelete : undefined}
            onSubmit={submitForm}
          />
        </FormLayout>
      )}
    </Formik>
  )
}

export {EntryForm}
